import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useLocation,
  useHistory
} from 'react-router-dom';
import AccessManagement from './pages/AccessManagement';
import AccessInternalManagement from './pages/AccessInternalManagement';
import ContractsAndConstructions from './pages/ContractsAndConstructions/contractsAndConstructions.routes';
import DashboardRoutes from './pages/Dashboard';
import DownloadArea from './pages/DownloadArea';
import Financials from './pages/Financials';
import FirstLogin from './pages/FirstLogin';
import { HelpCenter } from './pages/HelpCenter';
import OrdersRoutes from './pages/Orders/orders.routes';
import ProjectsAndMockupsRoutes from './pages/ProjectsMockup/projectsMockup.routes';
import PurchaseRoutes, {
  EPurchaseRoutes
} from './pages/Purchase/purchase.routes';
import ReportsDownload from './pages/ReportsDownload';
import Settings from './pages/Settings';
import { ApplicationState } from './store';
import { ECustomerTypeView } from './store/ducks/user/types';
import Loyalty from './pages/Loyalty';
import Header from './pages/Header';
import Notifications from './pages/Notifications';
import { Providers } from './providers/providers';
import UserEvaluation from './pages/UserEvaluation';
import Favorites from './pages/Favorites';
import { ESessionStorageKey } from './services/EGerdauService/types';
import { Manifestation } from './pages/Manifestation';
import { GetFlag } from './utils/featureFlag';
import { internalUserEmailValidate } from './utils/formatStrings';
import { NewHelpCenter } from './pages/NewHelpCenter';
import AppModals from './components/AppModals';
import MarketCommunicationModal from './components/MarketCommunicationModal';
import useCanGoToPurchase from './hooks/useCanGoToPurchase';

import useCmsBlockPrefetch from './hooks/useCmsBlockPrefetch';
import { CMS_BLOCK_ID as DASHBOARD_CMS_BLOCK_ID } from './pages/Dashboard/MarketCommunicationDashboard';
import { CMS_BLOCK_ID as MODAL_CMS_BLOCK_ID } from './components/MarketCommunicationModal';
import useIsCommerceUser from './hooks/useIsCommerceUser';
import useIsBusinessTypeCG from './hooks/useIsBusinessTypeCG';

export enum FaceliftRoutes {
  FACELIFT = '/facelift'
}

export enum ERoutes {
  ORDERS = '/orders',
  FINANCIALS = '/financials',
  PURCHASE = '/purchase',
  SETTINGS = '/settings',
  DASHBOARD = '/dashboard',
  PROJECT_MOCKUP = '/projects-mockup',
  MANIFESTATION = '/manifestation',
  CONTRACTS_AND_CONSTRUCTIONS = '/contracts-constructions',
  DOWNLOAD_AREA = '/download-area',
  HELP_CENTER = '/help-center',

  REPORTS_DOWNLOAD = '/reports/open-orders',
  ACCESS_MANAGEMENT = '/access-management',
  ACCESS_INTERNAL_MANAGEMENT = '/access-internal-management',
  FIRST_LOGIN = '/first-login',
  LOYALTY = '/loyalty',
  NOTIFICATIONS = '/notifications',
  SHOPPING_CART = '/shopping-cart',
  USER_EVALUATION = '/user-evaluation',
  FAVORITES = '/favorites'
}

interface IAccessControllRoutes {
  route: ERoutes;
  component: React.FC;
  hasUserPermission: boolean;
  customerViews: ECustomerTypeView[];
}

const Routes: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [canShowHeader, setCanShowHeader] = useState(false);

  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const userPermissions = useSelector(
    (state: ApplicationState) => state.user.userPermissions
  );
  const isManager = useSelector(
    (state: ApplicationState) => state.user.isManager
  );

  const userId = useSelector((state: ApplicationState) => state.user.id);

  const userEmail = useSelector((state: ApplicationState) => state.user.email);

  const salesRep = useSelector(
    (state: ApplicationState) => state.user.salesRep
  );

  const userLoaded = useSelector(
    (state: ApplicationState) => state.user.userLoaded
  );

  const isEmitter = customerViewType === ECustomerTypeView.EMITTER;
  const isFabricationReceiver =
    customerViewType === ECustomerTypeView.FABRICATION_RECEIVER;

  const canGoToPurchase = useCanGoToPurchase();

  const purchasePath = `${ERoutes.PURCHASE}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`;

  const homePage: string = canGoToPurchase
    ? purchasePath
    : !isEmitter && !isFabricationReceiver
    ? ERoutes.ORDERS
    : ERoutes.DASHBOARD;

  const isAdobeMode = GetFlag('market_communication_adobe');
  useCmsBlockPrefetch(
    isAdobeMode ? [DASHBOARD_CMS_BLOCK_ID, MODAL_CMS_BLOCK_ID] : []
  );

  const new_helpcenter = GetFlag('new_helpcenter');
  const favorite_product = GetFlag('favorite_product');
  const isMVPUser = useIsCommerceUser();
  const isBusinessTypeCG = useIsBusinessTypeCG();

  const manifestation = GetFlag('manifestation');
  const internalUsersList = GetFlag('internal_users');
  const isInternalUser = internalUserEmailValidate(userEmail);

  const internalUserManifestationAccess = isInternalUser && internalUsersList;
  const isManagerManifestationAccess = isManager && !salesRep;
  const basicUserManifestationAccess =
    userPermissions.hasFinancialAccess &&
    userPermissions.hasOrderViewAccess &&
    !isInternalUser &&
    !salesRep;

  const accessControllRoutes: IAccessControllRoutes[] = [
    {
      route: ERoutes.ORDERS,
      component: OrdersRoutes,
      hasUserPermission: userPermissions.hasOrderViewAccess || isManager,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.MANIFESTATION,
      component: Manifestation,
      hasUserPermission:
        (internalUserManifestationAccess ||
          isManagerManifestationAccess ||
          basicUserManifestationAccess) &&
        manifestation,
      customerViews: [ECustomerTypeView.EMITTER, ECustomerTypeView.RECEIVER]
    },
    {
      route: ERoutes.FINANCIALS,
      component: Financials,
      hasUserPermission: userPermissions.hasFinancialAccess || isManager,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.PURCHASE,
      component: PurchaseRoutes,
      hasUserPermission: userPermissions.hasOrderInputAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.DASHBOARD,
      component: DashboardRoutes,
      hasUserPermission:
        userPermissions.hasOrderViewAccess ||
        userPermissions.hasFinancialAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.ACCESS_MANAGEMENT,
      component: AccessManagement,
      hasUserPermission: userPermissions.hasAccessManagementAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.PROJECT_MOCKUP,
      component: ProjectsAndMockupsRoutes,
      hasUserPermission:
        userPermissions.hasOrderInputAccess &&
        userPermissions.hasFabricationAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.CONTRACTS_AND_CONSTRUCTIONS,
      component: ContractsAndConstructions,
      hasUserPermission:
        userPermissions.hasOrderInputAccess &&
        userPermissions.hasFabricationAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.DOWNLOAD_AREA,
      component: DownloadArea,
      hasUserPermission: true,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.HELP_CENTER,
      ...(new_helpcenter
        ? { component: NewHelpCenter }
        : { component: HelpCenter }),
      hasUserPermission: true,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.LOYALTY,
      component: Loyalty,
      hasUserPermission: userPermissions.hasLoyaltyAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.NOTIFICATIONS,
      component: Notifications,
      hasUserPermission: userPermissions.hasLoyaltyAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    }
  ];

  const CanShowInternalManagementRoute = useCallback(() => {
    // TODO change the permission.
    if (userPermissions.hasAccessManagementAccess) {
      return true;
    } else {
      return false;
    }
  }, [userPermissions]);

  useEffect(() => {
    const reportDownloadMatch = matchPath(location.pathname, {
      path: ERoutes.REPORTS_DOWNLOAD
    });
    const firstLoginMatch = matchPath(location.pathname, {
      path: ERoutes.FIRST_LOGIN
    });
    const userEvaluation = matchPath(location.pathname, {
      path: ERoutes.USER_EVALUATION
    });

    if (userEvaluation && !userId) {
      sessionStorage.setItem(
        ESessionStorageKey.USER_EVALUATION_URL,
        location.pathname + location.search
      );
    }

    if (
      (location.pathname === '/' || location.pathname === ERoutes.DASHBOARD) &&
      sessionStorage.getItem(ESessionStorageKey.USER_EVALUATION_URL)?.length
    ) {
      history.replace(
        sessionStorage.getItem(ESessionStorageKey.USER_EVALUATION_URL)!
      );
      sessionStorage.removeItem(ESessionStorageKey.USER_EVALUATION_URL);
    }

    setCanShowHeader(
      (!reportDownloadMatch || reportDownloadMatch.isExact === false) &&
        (!firstLoginMatch || firstLoginMatch.isExact === false) &&
        !userEvaluation
    );
  }, [location.pathname]);

  const purchaseLandingPath = `${ERoutes.PURCHASE}/${EPurchaseRoutes.COMMERCE}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`;
  const hasPendingUserEvaluation = !!sessionStorage.getItem(
    ESessionStorageKey.USER_EVALUATION_URL
  );
  const isOnLandingPage =
    !hasPendingUserEvaluation &&
    (location.pathname === ERoutes.DASHBOARD ||
      location.pathname.startsWith(purchaseLandingPath));

  const initialRedirectDone = useRef(false);

  useEffect(() => {
    if (!userLoaded || initialRedirectDone.current) return;
    const isAtEvaluation = location.pathname.includes(ERoutes.USER_EVALUATION);
    // evita sobrescrever o replace feito pelo efeito acima ao restaurar a URL de avaliação pendente
    if (isAtEvaluation || hasPendingUserEvaluation) return;
    initialRedirectDone.current = true;
    const isAtEntryPoint =
      location.pathname === '/' || location.pathname === ERoutes.DASHBOARD;
    if (!isAtEntryPoint) return;
    history.replace(homePage);
  }, [userLoaded, location.pathname, hasPendingUserEvaluation]);

  return (
    <Providers>
      {canShowHeader && <Header />}
      <Switch>
        {accessControllRoutes
          .filter(
            (route) =>
              route.hasUserPermission &&
              route.customerViews.includes(customerViewType)
          )
          .map((route) => (
            <Route
              key={route.route}
              path={route.route}
              component={route.component}
            />
          ))}

        {favorite_product && isMVPUser && !isBusinessTypeCG && (
          <Route path={ERoutes.FAVORITES} component={Favorites} />
        )}
        <Route path={ERoutes.SETTINGS} component={Settings} />
        <Route path={ERoutes.REPORTS_DOWNLOAD} component={ReportsDownload} />
        <Route path={ERoutes.FIRST_LOGIN} component={FirstLogin} />
        <Route path={ERoutes.USER_EVALUATION} component={UserEvaluation} />
        {CanShowInternalManagementRoute() && (
          <Route
            path={ERoutes.ACCESS_INTERNAL_MANAGEMENT}
            component={AccessInternalManagement}
          />
        )}

        <Route path="/" exact>
          {userLoaded && !hasPendingUserEvaluation && (
            <Redirect to={homePage} />
          )}
        </Route>
        {/* se não encontrar nenhuma rota */}
        {/* <Route path="*">
                        <Redirect to={homePage} />
                    </Route> */}
      </Switch>
      {userLoaded && isOnLandingPage && <AppModals />}
      {userLoaded && <MarketCommunicationModal />}
    </Providers>
  );
};

export default Routes;
